import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';

import { ModalNextProvider, Loader } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { ServiceManager } from '@jutro/services';
import { error } from '@jutro/logger';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { LobIconUtil } from 'gw-portals-util-js';
import {
    ImageComponent
} from 'gw-components-platform-react';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';

import metadata from './BillingSummaryPage.metadata.json5';
import styles from './BillingSummaryPage.module.scss';
import messages from './BillingSummaryPage.messages';

function BillingSummaryPage(props) {
    const {
        match, authHeader, history, isAuthorisedUser
    } = props;
    const { params } = match;
    const { streamId } = params;
    const { location: { state: dataProps } } = history;

    const localeService = ServiceManager.getService('locale-service');
    const defaultCurrencyCode = localeService.getDefaultCurrencyCode().toUpperCase();

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    const { BillingService: DocumentBillingService } = useDependencies('BillingService');

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(true);
    const [selectedInvoiceStreamId, updateSelectedInvoiceStreamId] = useState(streamId);
    const [selectedCurrency, updateSelectedCurrency] = useState(defaultCurrencyCode);
    const [tabResult, setTabResult] = useState({});
    const [documentDetails, setDocumentDetails] = useState([]);
    const [isInitalStreamCallInvoked, setInitalStreamCallInvoked] = useState(false);

    // fetched data
    const [accountBillingSummaryFromApi, setAccountBillingSummaryFromApi] = useState({});
    const [invoiceStreamDetailsMapFromApi, setInvoiceStreamDetailsMapFromApi] = useState(new Map());
    const [paymentInstrumentPlan, setPaymentInstrumentPlan] = useState({});

    const {
        accountLevelBilling = false,
        invoiceStreams_Ext: invoiceStreams = []
    } = accountBillingSummaryFromApi;
    const updateMap = useCallback(
        (invoiceStreamId, billingResponse) => {
            setInvoiceStreamDetailsMapFromApi(
                invoiceStreamDetailsMapFromApi.set(invoiceStreamId, billingResponse)
            );

            const { totalValue } = billingResponse;
            const { currency } = totalValue;
            updateSelectedCurrency(currency.toUpperCase());
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const getPolicyHeaderIconVisible = useCallback((policyId, type) => {
        // eslint-disable-next-line no-nested-ternary
        return type === ''
            ? (policyId === selectedInvoiceStreamId ? styles.headerIconHide : '')
            : (policyId !== selectedInvoiceStreamId ? styles.headerIconHide : '');
    }, [selectedInvoiceStreamId]);

    const getTabHeader = useCallback((item) => {
        const {
            lobcode: productCode, label, internalId: policyId, offeringCode, offeringName
        } = item;
        if (_.isNil(label)) {
            return null;
        }
        const policyTypeAbbreviation = _.get(appConfig, `TDICAbbreviations.${offeringCode}`, '');
        return (
            <div id={`divFor${productCode}${label}`} className={styles.tabContent}>
                <ImageComponent
                    src={`/branding/${LobIconUtil.getImageIcon(productCode, offeringCode)}.svg`}
                    alt={productCode}
                    title={offeringName}
                    className={getPolicyHeaderIconVisible(policyId, '')}
                />
                <ImageComponent
                    src={`/branding/${LobIconUtil.getImageIcon(productCode, offeringCode)}-filled.svg`}
                    alt={productCode}
                    title={offeringName}
                    className={getPolicyHeaderIconVisible(policyId, 'filled')}
                />
                <span className={styles.headerPolicy}>{policyTypeAbbreviation}</span>
                <span id={`labelFor${productCode}${label}`} className={styles.headerPolicy}>{label.toUpperCase()}</span>
            </div>
        );
    }, [getPolicyHeaderIconVisible]);

    const fetchAccountLevelInvoiceStreams = useCallback(() => {
        let accountInvoicePromise;

        if (_.isEmpty(accountBillingSummaryFromApi)) {
            accountInvoicePromise = BillingService.getAccountInvoiceStreamInformationExt(
                authHeader
            ).then((accountInvoiceStreams) => {
                const {
                    invoiceStreams_Ext: invoiceStreamsFromApi = []
                } = accountInvoiceStreams;

                let defaultInvoiceStreamId;
                let defaultPolicyNumber;
                if (invoiceStreamsFromApi.length > 0) {
                    defaultInvoiceStreamId = invoiceStreamsFromApi[0].internalId;
                    defaultPolicyNumber = invoiceStreamsFromApi[0].label;
                }

                updateSelectedInvoiceStreamId(defaultInvoiceStreamId);
                setAccountBillingSummaryFromApi(accountInvoiceStreams);
                const responseObject = {
                    defaultInvoiceStreamId: defaultInvoiceStreamId,
                    defaultPolicyNumber: defaultPolicyNumber
                };
                return responseObject;
            });
        } else {
            accountInvoicePromise = Promise.resolve(accountBillingSummaryFromApi);
        }

        return Promise.all([accountInvoicePromise]).catch((err) => {
            error(`BillingSummaryPage: fetchAccountLevelInvoiceStreams - Unable to fetch Account Level Invoice Streams ${JSON.stringify(err)}`);
            ModalNextProvider.showAlert({
                title: messages.failedToLoadPolices,
                message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                history.push('/home');
            }, _.noop);
        });
    }, [BillingService, history, authHeader, accountBillingSummaryFromApi]);

    const getPaymentDetailsGroupFromMap = useCallback(
        (invoiceStreamId) => {
            return invoiceStreamDetailsMapFromApi.get(invoiceStreamId);
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const getPolicyPaymentPlan = useCallback((policyNumber) => {
        if (!policyNumber || isAuthorisedUser) {
            return;
        }
        BillingService.getPolicyPaymentPlan(
            policyNumber,
            authHeader
        ).then((result) => {
            setPaymentInstrumentPlan(result);
        });
    }, [BillingService, authHeader, isAuthorisedUser]);

    const getDocumentDetails = useCallback((policyNumber) => {
        DocumentBillingService.getDocumentDetails(policyNumber, authHeader)
            .then((documentsResponse) => {
                setDocumentDetails(documentsResponse);
            });
    }, [DocumentBillingService, authHeader]);

    const fetchInvoiceDetailsByStreamId = useCallback(
        (invoiceStreamId, forceRefresh = false) => {
            const invoiceDetails = getPaymentDetailsGroupFromMap(invoiceStreamId);
            let invoiceDetailsPromise;

            if ((!isInitalStreamCallInvoked && invoiceStreamId
                && _.isEmpty(invoiceDetails)) || forceRefresh) {
                setInitalStreamCallInvoked(true);
                invoiceDetailsPromise = BillingService.getInvoiceStreamDetails(
                    invoiceStreamId,
                    authHeader
                )
                    .then((billingResponse) => {
                        updateMap(invoiceStreamId, billingResponse);
                        setTabResult(billingResponse);
                        // Payment plan should call when forceRefresh is true
                        if (forceRefresh && !_.isEmpty(billingResponse)) {
                            updateSelectedInvoiceStreamId(invoiceStreamId);
                            const policyNumber = _.get(billingResponse, 'policyNumber');
                            getPolicyPaymentPlan(policyNumber);
                        }
                    })
                    .catch((err) => {
                        error(`BillingSummaryPage: fetchInvoiceDetailsByStreamId - Unable to fetch Invoice Details ${JSON.stringify(err)}`);
                        ModalNextProvider.showAlert({
                            title: messages.failedToLoadPolices,
                            message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).then(() => {
                            history.push('/home');
                        }, _.noop);
                    });
            } else {
                invoiceDetailsPromise = Promise.resolve(invoiceDetails);
            }
            return invoiceDetailsPromise;
        },
        [getPaymentDetailsGroupFromMap, isInitalStreamCallInvoked, BillingService, authHeader,
            updateMap, getPolicyPaymentPlan, history]
    );

    const selectedLobCode = useMemo(() => {
        const foundInvoiceStreams = invoiceStreams.filter(
            (item) => item.internalId === selectedInvoiceStreamId
        );
        return foundInvoiceStreams.length > 0 ? foundInvoiceStreams[0].lobcode : undefined;
    }, [invoiceStreams, selectedInvoiceStreamId]);

    const onTabClick = useCallback(
        (invoiceStreamIdFromTabId) => {
            fetchInvoiceDetailsByStreamId(invoiceStreamIdFromTabId,
                true);
        },
        [fetchInvoiceDetailsByStreamId]
    );

    const reloadInvoiceDetails = useCallback(
        (invoiceStreamId) => {
            updateIsLoadingData(true);
            return fetchInvoiceDetailsByStreamId(invoiceStreamId, true).finally(() => {
                updateIsLoadingData(false);
            });
        },
        [fetchInvoiceDetailsByStreamId]
    );

    useEffect(() => {
        updateIsLoadingData(true);
        fetchAccountLevelInvoiceStreams().then((response) => {
            if (!_.isEmpty(response)) {
                const { defaultInvoiceStreamId, defaultPolicyNumber } = _.get(response, '[0]', {});
                const initStreamId = selectedInvoiceStreamId || defaultInvoiceStreamId;
                fetchInvoiceDetailsByStreamId(initStreamId, false).finally(() => {
                    updateIsLoadingData(false);
                });
                getPolicyPaymentPlan(defaultPolicyNumber);
            }
        });
        // execure once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const noData = useCallback(
        () => {
            return invoiceStreams.length === 0;
        },
        [invoiceStreams]
    );

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        noDataMessageContainer: {
            visible: noData(),
            message: messages.noBillingData
        },
        billingSummaryContainer: {
            visible: !noData(),
        },
        billingTabsId: {
            content: _.map(invoiceStreams, (item) => {
                return {
                    id: item.internalId,
                    type: 'element',
                    component: 'tab',
                    componentProps: {
                        heading: getTabHeader(item)
                    },
                    content: [
                        {
                            id: `billingContentFor${item.internalId}`,
                            type: 'container',
                            component: 'BillingSummary',
                            componentProps: {
                                accountLevelBilling: accountLevelBilling,
                                invoiceStreamId: selectedInvoiceStreamId,
                                lobCode: selectedLobCode,
                                paymentGroupDetails: getPaymentDetailsGroupFromMap(
                                    selectedInvoiceStreamId
                                ),
                                currency: selectedCurrency,
                                reloadInvoiceDetails: reloadInvoiceDetails,
                                showManagePayment: _.get(dataProps, 'managePaymentPlan', null),
                                paymentInstrumentPlan: paymentInstrumentPlan,
                                tabResult,
                                callbackToGetPaymentPlan: getPolicyPaymentPlan,
                                documentDetails,
                                callbackToGetBillingDocument: getDocumentDetails,
                            }
                        }
                    ]
                };
            }),
            activeTab: selectedInvoiceStreamId,
            onTabChange: onTabClick
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

BillingSummaryPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired,
    isAuthorisedUser: PropTypes.bool.isRequired
};

export
default withAuthenticationContext(withRouter(withApplicationPropertyContext(BillingSummaryPage)));
